import { z } from 'zod';
import { consumptionSection } from '../fuel/consumption.js';
import { PORT_CODE_PATTERN, portCodeField } from '../ports/catalog.js';
import {
  BERTH_SIDES,
  CARGO_OPERATIONS,
  CARGO_PORT_OPERATIONS,
  CARGO_UNITS,
  CERTIFICATE_GRADES,
  CHECKLIST_STATUSES,
  COMPASS_POINTS,
  CONDITION_GRADES,
  CURRENT_RELATIONS,
  DAILY_ACTIVITIES,
  DEPARTURE_PURPOSES,
  EMPLOYMENT_STATUSES,
  EU_ROUTES,
  FUEL_TYPES,
  LOAD_CONDITIONS,
  LUBE_TYPES,
  MACHINERY_STATUSES,
  NEXT_OPERATIONS,
  PROPELLER_TYPES,
  VISIBILITY_GRADES,
  VOYAGE_ACTIONS,
  WAITING_REASONS,
} from './lists.js';
import { nextOperationOptions } from './next-operation.js';
import { ownerReferenceSchema } from './reference.js';

/**
 * Sefer raporlarının şemaları (ADR 0022 §2). Ortak alanlar tablonun sütunlarıdır; türün alanları
 * `data` içinde durur. Aynı şema gemi sunucusunda, bulutta ve formda kullanılır. Hata iletileri
 * koddur (`REQUIRED`, `OUT_OF_RANGE` …); arayüz `voyage.errors.<kod>` ile çevirir. Zorunlu alanlar
 * müşterinin rapor alan matrisindeki "Zorunlu" sütunu ile ekrandaki yıldızların birleşimidir
 * (daha sıkı olan; CLAUDE.md "Çalışma şekli" 5).
 */

/**
 * `data` sütununun şema sürümü; alan eklenip çıkarılınca artar (ADR 0022 §2). Sürüm 2 (ADR 0058
 * §1): Departure'ın olay zamanı Cast Off, pilot off ayrı alan. Sürüm 1 raporlar eski anlamıyla
 * okunur; kayıt katmanı ikisini de kabul eder.
 */
export const VOYAGE_REPORT_SCHEMA_VERSION = 2;
export const VOYAGE_REPORT_SCHEMA_VERSIONS: readonly number[] = [1, 2];

export const VOYAGE_REPORT_KINDS = [
  'departure',
  'cosp',
  'noon',
  'eosp',
  'arrival',
  // Limanda günlük (ADR 0059 §1)
  'port_daily',
  'anchorage_start',
  'anchorage_daily',
  'anchorage_end',
  'waiting_start',
  'waiting_end',
  'drift_start',
  // Sürüklenmede günlük (ADR 0059 §1)
  'drift_daily',
  'drift_end',
  'strait_entry',
  'strait_exit',
  'canal_entry',
  'canal_exit',
  'cargo_start',
  'cargo_complete',
  'on_hire',
  'off_hire',
] as const;
export type VoyageReportKind = (typeof VOYAGE_REPORT_KINDS)[number];

/**
 * Konumu zorunlu türler (müşterinin matrisi: Departure, COSP, Noon, EOSP, Anchorage Daily; Drift
 * Daily de demirdeki gibi: ADR 0059 §3). Port Daily'de rıhtım liman ve rıhtımla bellidir.
 */
export const POSITION_REQUIRED: ReadonlySet<VoyageReportKind> = new Set([
  'departure',
  'cosp',
  'noon',
  'eosp',
  'anchorage_daily',
  'drift_daily',
]);

/** Seferin günlük raporları: limanda, demirde, sürüklenmede (ADR 0059 §1). */
export const DAILY_KINDS = ['port_daily', 'anchorage_daily', 'drift_daily'] as const;
export type DailyKind = (typeof DAILY_KINDS)[number];
export const isDailyKind = (kind: string): kind is DailyKind =>
  (DAILY_KINDS as readonly string[]).includes(kind);

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});

function num(min: number, max: number) {
  return z.number(typed('INVALID_NUMBER')).min(min, 'OUT_OF_RANGE').max(max, 'OUT_OF_RANGE');
}
function int(min: number, max: number) {
  return num(min, max).int('NOT_INTEGER');
}
/** Yön (derece): 0 ≤ x < 360. */
const direction = () =>
  z.number(typed('INVALID_NUMBER')).min(0, 'OUT_OF_RANGE').lt(360, 'OUT_OF_RANGE');
const text = (max = 200) =>
  z.string(typed('INVALID_TEXT')).trim().min(1, 'REQUIRED').max(max, 'TOO_LONG');
const note = () => z.string(typed('INVALID_TEXT')).trim().max(500, 'TOO_LONG').nullish();
/** UTC ISO-8601; saat farkı yazılmaz (CLAUDE.md "Zaman"). */
const datetime = () => z.iso.datetime(typed('INVALID_DATETIME'));
const option = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values, typed('INVALID_OPTION'));
const object = <S extends z.ZodRawShape>(shape: S) => z.object(shape, typed('INVALID_OBJECT'));
const list = <T extends z.ZodType>(item: T) => z.array(item, typed('INVALID_LIST'));

/**
 * Gemi ya da liman saati farkı: dakika hassasiyeti, −12:00…+14:00 (Noon Report "Time Zone").
 * Eski 30 dakikalık değerler geçerli kalır; +05:45 ya da vardiyalara bölünmüş geri almanın
 * ara farkları da yazılabilir (ADR 0065 §8).
 */
const tzOffset = () =>
  z
    .number(typed('INVALID_TZ_OFFSET'))
    .refine((v) => Number.isInteger(v) && v >= -720 && v <= 840, 'INVALID_TZ_OFFSET');

const tonnes = () => num(0, 100_000);
const speed = () => num(0, 40);

/**
 * ROB satırı: yakıt ve kalan (MT). Önceki, tüketim ve not isteğe bağlı (ROB tablosu; ADR 0057
 * §3); CP hesabı yalnız `fuel` ve `rob`'u okur.
 */
const robRow = object({
  fuel: option(FUEL_TYPES),
  rob: tonnes(),
  previousMt: tonnes().nullish(),
  consumptionMt: tonnes().nullish(),
  /** Günlük raporda bugünkü tüketim (ADR 0059 §3); `consumptionMt` son rapordan beri. */
  todayMt: tonnes().nullish(),
  /**
   * Son rapordan beri ikmal: defteri açık gemide anlık görüntü yazar (aralıktaki onaylı alım;
   * ADR 0090 Ek). Önceki − tüketim + ikmal = ROB.
   */
  bunkeredMt: tonnes().nullish(),
  remarks: z.string().trim().max(100, 'TOO_LONG').nullish(),
});
const robRows = () =>
  list(robRow)
    .superRefine((rows, ctx) => uniqueBy(rows, (r) => r.fuel, 'fuel', ctx))
    .nullish();

function uniqueBy<T>(
  rows: readonly T[],
  key: (row: T) => string,
  field: string,
  ctx: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  rows.forEach((row, index) => {
    const value = key(row);
    if (seen.has(value)) {
      ctx.addIssue({ code: 'custom', message: 'DUPLICATE', path: [index, field] });
    }
    seen.add(value);
  });
}

/* ---------- Ortak bölümler (ADR 0057 §3; isteğe bağlı, şema sürümü 1) ---------- */

/** Römorkör satırları: ad, çekme gücü (t), alongside / made fast ve cast off / let go (UTC). */
const tugRows = () =>
  list(
    object({
      name: text(60),
      bollardPullT: num(0, 200).nullish(),
      alongside: datetime().nullish(),
      castOff: datetime().nullish(),
    }),
  )
    .max(10, 'TOO_MANY')
    .nullish();

/** Yağ ROB'u (MT; ortağın cevabı #3): tür başına tek satır. */
const lubeRobRows = () =>
  list(
    object({
      lube: option(LUBE_TYPES),
      rob: tonnes(),
      previousMt: tonnes().nullish(),
      consumptionMt: tonnes().nullish(),
    }),
  )
    .superRefine((rows, ctx) => uniqueBy(rows, (r) => r.lube, 'lube', ctx))
    .nullish();

/** Tatlı su ROB'u (MT). */
const freshWaterRob = () =>
  object({
    rob: tonnes(),
    previousMt: tonnes().nullish(),
    consumptionMt: tonnes().nullish(),
    todayMt: tonnes().nullish(),
  }).nullish();

/** Hava: rüzgâr yönü (16 yön) ve Beaufort, Douglas deniz durumu, görüş, sıcaklıklar. */
const weatherBlock = () =>
  object({
    windDirection: option(COMPASS_POINTS).nullish(),
    beaufort: int(0, 12).nullish(),
    seaState: int(0, 9).nullish(),
    visibility: option(VISIBILITY_GRADES).nullish(),
    airTempC: num(-60, 60).nullish(),
    seaTempC: num(-5, 40).nullish(),
  }).nullish();

/** Gemideki kişiler: mürettebat ve yolcu/diğer. */
const personsBlock = () =>
  object({ crew: int(0, 999).nullish(), passengers: int(0, 9999).nullish() }).nullish();

/**
 * Gemi ve donanım durumu. "Unsatisfactory" ya da süresi geçmiş sertifika varsa açıklama zorunlu
 * (daha sıkı okuma; ADR 0057 §3).
 */
const shipConditionBlock = () =>
  object({
    hull: option(CONDITION_GRADES).nullish(),
    machinery: option(CONDITION_GRADES).nullish(),
    navigation: option(CONDITION_GRADES).nullish(),
    safety: option(CONDITION_GRADES).nullish(),
    classCertificates: option(CERTIFICATE_GRADES).nullish(),
    statutoryCertificates: option(CERTIFICATE_GRADES).nullish(),
    remarks: note(),
  })
    .superRefine((value, ctx) => {
      const grades: unknown[] = Object.values(value);
      if (
        (grades.includes('unsatisfactory') || grades.includes('expired')) &&
        !(value.remarks ?? '').trim()
      ) {
        ctx.addIssue({ code: 'custom', message: 'REQUIRED', path: ['remarks'] });
      }
    })
    .nullish();

/* ---------- Kalkış ve varış blokları (ADR 0058 §2, §6; isteğe bağlı, sürüm 2) ---------- */

const optText = (max = 200) =>
  z.string(typed('INVALID_TEXT')).trim().max(max, 'TOO_LONG').nullish();
const quantity = () => num(0, 10_000_000).nullish();
const flag = () => z.boolean(typed('INVALID_OPTION')).nullish();

/** Bağlı kayıt: kimlik ve görevlinin gördüğü etiket (rapor ve PDF ek sorgusuz okunur; §2). */
const linkRef = () =>
  object({
    id: z.uuid(typed('INVALID_ID')),
    label: z.string(typed('INVALID_TEXT')).trim().max(200, 'TOO_LONG'),
  });

/** Limandaki yük işlemi ve satırları (tel kafes: miktar ve birim ayrı). */
const cargoBlock = () =>
  object({
    operation: option(CARGO_PORT_OPERATIONS).nullish(),
    lines: list(
      object({
        cargoType: text(100),
        loadedQty: quantity(),
        dischargedQty: quantity(),
        unit: option(CARGO_UNITS),
      }),
    )
      .max(10, 'TOO_MANY')
      .nullish(),
    onBoardQty: quantity(),
    onBoardUnit: option(CARGO_UNITS).nullish(),
    remarks: note(),
  }).nullish();

/** NOR (BIMCO Laytime Definitions 2013): veriliş, kabul, laytime başlangıcı. */
const norBlock = () =>
  object({
    tenderedAt: datetime().nullish(),
    tenderedTo: optText(100),
    acceptedAt: datetime().nullish(),
    acceptedBy: optText(100),
    laytimeCommencedAt: datetime().nullish(),
    remarks: note(),
  }).nullish();

/** Halatlar (arrival-report.jpeg "Tugs & Mooring"). */
const mooringBlock = () =>
  object({
    lines: int(0, 99).nullish(),
    shoreLines: int(0, 99).nullish(),
    remarks: note(),
  }).nullish();

/** Kalkış sınıflandırması (tel kafes "Departure Classification"); MRV durumu türetilir. */
const classificationBlock = () =>
  object({
    employment: option(EMPLOYMENT_STATUSES).nullish(),
    purpose: option(DEPARTURE_PURPOSES).nullish(),
    purposeOther: optText(200),
    charterReference: optText(60),
    euRoute: option(EU_ROUTES).nullish(),
    euRouteManual: flag(),
  }).nullish();

/** Kalkış kontrol listesi: özet ve ISM Forms kaydına bağ. */
const checklistBlock = () =>
  object({
    status: option(CHECKLIST_STATUSES).nullish(),
    formRecordId: z.uuid(typed('INVALID_ID')).nullish(),
    formRecordLabel: optText(200),
    remarks: note(),
  }).nullish();

/** Açık arızalar / kısıtlar: PMS arızalarına bağ ve kısıt açıklaması. */
const openDefectsBlock = () =>
  object({ items: list(linkRef()).max(20, 'TOO_MANY').nullish(), remarks: note() }).nullish();

/**
 * Rapordaki liman kodu (ADR 0048 §3): isteğe bağlı; yoksa alan hiç yazılmaz (eski raporların
 * verisi ve içerik hash'i değişmez).
 */
const reportPortCode = () =>
  z.string(typed('INVALID_PORT_CODE')).regex(PORT_CODE_PATTERN, 'INVALID_PORT_CODE').nullish();

/* ---------- Seferin günlük raporları (ADR 0059 §3; isteğe bağlı, sürüm 2) ---------- */

/** Konum durumu (`g1.jpeg` "Location Status"): liman, terminal / rıhtım / alan, faaliyet, taraf. */
const locationBlock = () =>
  object({
    port: optText(200),
    portCode: reportPortCode(),
    area: optText(200),
    activity: option(DAILY_ACTIVITIES).nullish(),
    berthSide: option(BERTH_SIDES).nullish(),
  }).nullish();

/**
 * Yük ilerlemesi (`g1.jpeg` "Cargo / Operation"): planlanan, son rapordan beri, toplam; kalan
 * türetilir (planlanan − toplam).
 */
const dailyCargoBlock = () =>
  object({
    operation: option(CARGO_PORT_OPERATIONS).nullish(),
    cargoType: optText(100),
    unit: option(CARGO_UNITS).nullish(),
    loadCondition: option(LOAD_CONDITIONS).nullish(),
    plannedQty: quantity(),
    handledSinceLastQty: quantity(),
    handledTotalQty: quantity(),
    remarks: note(),
  }).nullish();

/** Makine çalışma saatleri (`g1.jpeg` §7): son rapordan beri, bugün (≤ 24), toplam sayaç. */
const runningHourRows = () =>
  list(
    object({
      equipment: text(60),
      sinceLastH: num(0, 10_000).nullish(),
      todayH: num(0, 24).nullish(),
      totalH: num(0, 1_000_000).nullish(),
      remarks: z.string().trim().max(100, 'TOO_LONG').nullish(),
    }),
  )
    .max(20, 'TOO_MANY')
    .superRefine((rows, ctx) =>
      uniqueBy(rows, (r) => r.equipment.toLocaleLowerCase('en'), 'equipment', ctx),
    )
    .nullish();

/** Sonraki işlem; türün seçeneklerinden olması `parseVoyageReport`'ta denetlenir. */
const nextOperation = () => option(NEXT_OPERATIONS).nullish();

/**
 * ROB tablosu ve tüketim bölümü (ADR 0117 §2.1): `consumption` isteğe bağlıdır; şema sürümü ve
 * eski raporların verisi değişmez.
 */
const robShape = () => ({
  rob: robRows(),
  lubes: lubeRobRows(),
  freshWater: freshWaterRob(),
  consumption: consumptionSection(),
});
const flowShape = () => ({ nextOperation: nextOperation() });

/** Üç günlük türün ortak alanları (ADR 0059 §3). */
const dailyShape = () => ({
  location: locationBlock(),
  etd: datetime().nullish(),
  etdRemarks: optText(200),
  cargoProgress: dailyCargoBlock(),
  ...robShape(),
  runningHours: runningHourRows(),
  weather: weatherBlock(),
  remarks: note(),
  ...flowShape(),
});

const fuelRow = object({
  fuel: option(FUEL_TYPES),
  opening: tonnes(),
  consumption: tonnes(),
  bunkered: tonnes(),
});
const lubeRow = object({
  name: text(60),
  opening: tonnes(),
  consumption: tonnes(),
  bunkered: tonnes(),
});

const noon = object({
  navigation: object({
    course: direction(),
    heading: direction(),
    sog: speed(),
    stw: speed(),
    distanceSinceLast: num(0, 1200),
    distanceThroughWater: num(0, 1200),
    distanceOverGround: num(0, 1200),
    distanceToGo: num(0, 30_000),
    nextWaypoint: z.string().trim().max(60, 'TOO_LONG').nullish(),
    // Ortağın Noon'u (`g3.jpeg` §7; ADR 0059 §5): isteğe bağlı, eski raporlar değişmez
    chartArea: optText(60),
    nextPort: optText(200),
    nextPortCode: reportPortCode(),
    /** Sonraki limana ETA (UTC; gelecek olabilir). */
    eta: datetime().nullish(),
    remarks: note(),
  }),
  machinery: object({
    mainEngine: object({
      rpm: num(0, 300),
      loadPct: num(0, 110),
      shaftRpm: num(0, 300),
      runningHours: num(0, 1_000_000).nullish(),
      /** Son rapordan beri (h; ADR 0059 §5); PMS önerisi yalnız `runningHours`'u okur. */
      hoursSinceLast: num(0, 10_000).nullish(),
      powerKw: num(0, 100_000).nullish(),
      status: option(MACHINERY_STATUSES).nullish(),
    }),
    propeller: object({
      type: option(PROPELLER_TYPES).nullish(),
      pitchM: num(0, 20),
      diameterM: num(0, 15).nullish(),
    }),
    auxiliaries: list(
      object({
        name: text(60),
        rpm: num(0, 3000).nullish(),
        loadPct: num(0, 110).nullish(),
        runningHours: num(0, 1_000_000).nullish(),
        hoursSinceLast: num(0, 10_000).nullish(),
        fuelConsumptionMt: tonnes().nullish(),
        status: option(MACHINERY_STATUSES).nullish(),
        remarks: z.string().trim().max(100, 'TOO_LONG').nullish(),
      }),
    ).nullish(),
    remarks: note(),
  }),
  fuel: object({
    fuels: list(fuelRow)
      .min(1, 'REQUIRED')
      .superRefine((rows, ctx) => uniqueBy(rows, (r) => r.fuel, 'fuel', ctx)),
    lubes: list(lubeRow)
      .superRefine((rows, ctx) => uniqueBy(rows, (r) => r.name, 'name', ctx))
      .nullish(),
    freshWater: object({
      rob: tonnes(),
      consumption: tonnes(),
      produced: tonnes(),
      bunkered: tonnes(),
    }).nullish(),
  }),
  /** Tüketim bölümü (ADR 0117 §2.1); isteğe bağlı. */
  consumption: consumptionSection(),
  weather: object({
    wind: object({ direction: direction(), speedKn: num(0, 200), beaufort: int(0, 12) }),
    sea: object({
      /** Douglas deniz durumu 0–9. */
      state: int(0, 9),
      swellDirection: direction(),
      swellHeightM: num(0, 30),
      swellPeriodS: num(0, 30).nullish(),
    }),
    current: object({
      speedKn: num(0, 15),
      direction: direction().nullish(),
      relation: option(CURRENT_RELATIONS).nullish(),
    }),
    visibilityNm: num(0, 100).nullish(),
    airTempC: num(-60, 60).nullish(),
    seaTempC: num(-5, 40).nullish(),
    pressureHpa: num(870, 1090).nullish(),
    condition: z.string().trim().max(60, 'TOO_LONG').nullish(),
    remarks: note(),
  }),
  ...flowShape(),
});

/**
 * Bir önceki rapordan bu yana gidilen mesafe (nm, parakete ya da GPS). Noon'da zorunlu; denizde
 * olan diğer olaylarda isteğe bağlı. Segment mesafeleri (CP, boğaz, kanal, genel) bunların
 * toplamıdır (ADR 0022 §4).
 */
const leg = () => num(0, 1200).nullish();

const draft = object({ fwdM: num(0, 30), aftM: num(0, 30) });

/** Boğaz ve kanal girişi ve çıkışı: ad, mesafe, ROB (g5 "5D"), açıklama, sonraki işlem. */
const passage = () =>
  object({
    name: text(),
    distanceSinceLast: leg(),
    ...robShape(),
    remarks: note(),
    ...flowShape(),
  });

/**
 * Sürüm 1 Departure (ADR 0022, 0054, 0057): rapor zamanı pilotun ayrılışı, `letGo` son halat.
 * Eski raporlar bununla okunur (ADR 0058 §1).
 */
const departureV1 = object({
  port: text(),
  portCode: reportPortCode(),
  terminal: z.string().trim().max(200, 'TOO_LONG').nullish(),
  allFast: datetime().nullish(),
  pilotOnBoard: datetime(),
  letGo: datetime(),
  underway: datetime().nullish(),
  /** Römorkör sayısı ve adları (ADR 0054 §4); isteğe bağlı, eski raporlar değişmez. */
  tugsUsed: int(0, 10).nullish(),
  tugDetails: z.string().trim().max(200, 'TOO_LONG').nullish(),
  draft: draft.nullish(),
  tugs: tugRows(),
  ...robShape(),
  weather: weatherBlock(),
  persons: personsBlock(),
  shipCondition: shipConditionBlock(),
  remarks: note(),
  ...flowShape(),
});
export const departureV1Schema = departureV1;

/** Sonraki liman (bilinmiyorsa gerekçe), ETA (UTC) ve mesafe. */
const nextPortShape = () => ({
  nextPort: optText(200),
  nextPortCode: reportPortCode(),
});

/**
 * Rapor zamanı (`reportedAt`) her türde olayın kendisidir. Departure'da sürüm 2'den beri **Cast
 * Off** (son halat; ortağın cevabı #1, ADR 0058 §1); sürüm 1'de pilotun ayrılışı.
 */
const DATA_SCHEMAS = {
  departure: object({
    port: text(),
    portCode: reportPortCode(),
    terminal: optText(200),
    ...nextPortShape(),
    nextPortEta: datetime().nullish(),
    distanceToNextPortNm: num(0, 30_000).nullish(),
    nextPortUnknownReason: optText(200),
    pilotOnBoard: datetime().nullish(),
    pilotOff: datetime().nullish(),
    /** Pilot kullanılmadı (N/A; tel kafes Ç3). */
    pilotNotUsed: flag(),
    tugsUsed: int(0, 10).nullish(),
    tugs: tugRows(),
    draft: draft.nullish(),
    loadCondition: option(LOAD_CONDITIONS).nullish(),
    airDraftM: num(0, 100).nullish(),
    displacementMt: num(0, 1_000_000).nullish(),
    cargo: cargoBlock(),
    classification: classificationBlock(),
    voyageAction: option(VOYAGE_ACTIONS).nullish(),
    noVoyageReason: optText(500),
    checklist: checklistBlock(),
    openDefects: openDefectsBlock(),
    portCall: linkRef().nullish(),
    ...robShape(),
    weather: weatherBlock(),
    persons: personsBlock(),
    shipCondition: shipConditionBlock(),
    remarks: note(),
    ...flowShape(),
  }),
  cosp: object({
    course: direction().nullish(),
    rpm: num(0, 300).nullish(),
    speedKn: speed().nullish(),
    loadCondition: option(LOAD_CONDITIONS).nullish(),
    ...robShape(),
    weather: weatherBlock(),
    remarks: note(),
    ...flowShape(),
  }),
  noon,
  eosp: object({
    distanceSinceLast: leg(),
    /** Limana kalan mesafe (arrival-report.jpeg "Distance to Port"; ADR 0058 §6). */
    distanceToPortNm: num(0, 1000).nullish(),
    nor: norBlock(),
    ...robShape(),
    weather: weatherBlock(),
    remarks: note(),
    ...flowShape(),
  }),
  // Berthing (ortağın "Arrival" 5A): olay zamanı yanaşma (all fast); sürüm 2 alanları ADR 0058 §6
  arrival: object({
    port: text(),
    portCode: reportPortCode(),
    terminal: optText(200),
    ...nextPortShape(),
    pilotOnBoard: datetime().nullish(),
    firstLine: datetime().nullish(),
    allFast: datetime().nullish(),
    pilotOff: datetime().nullish(),
    berthSide: option(BERTH_SIDES).nullish(),
    draft: draft.nullish(),
    airDraftM: num(0, 100).nullish(),
    distanceToShoreM: num(0, 1000).nullish(),
    loadCondition: option(LOAD_CONDITIONS).nullish(),
    nor: norBlock(),
    mooring: mooringBlock(),
    cargo: cargoBlock(),
    portCall: linkRef().nullish(),
    tugs: tugRows(),
    ...robShape(),
    weather: weatherBlock(),
    persons: personsBlock(),
    shipCondition: shipConditionBlock(),
    remarks: note(),
    ...flowShape(),
  }),
  anchorage_start: object({
    reason: option(WAITING_REASONS),
    distanceSinceLast: leg(),
    // Demirde bekleyen gemi NOR'u demirde verir (ADR 0058 §6)
    nor: norBlock(),
    ...robShape(),
    weather: weatherBlock(),
    remarks: note(),
    ...flowShape(),
  }),
  // Limanda günlük (ADR 0059 §1, §3)
  port_daily: object({ ...dailyShape() }),
  anchorage_daily: object({
    /** Sürüm 2 öncesi tek sayılık tüketim; eski raporlarda kalır, yeni form ROB tablosunu kullanır. */
    consumptionMt: tonnes().nullish(),
    ...dailyShape(),
  }),
  anchorage_end: object({ ...robShape(), remarks: note(), ...flowShape() }),
  waiting_start: object({
    reason: option(WAITING_REASONS),
    distanceSinceLast: leg(),
    weather: weatherBlock(),
    remarks: note(),
    ...flowShape(),
  }),
  waiting_end: object({ distanceSinceLast: leg(), remarks: note(), ...flowShape() }),
  drift_start: object({
    reason: option(WAITING_REASONS),
    distanceSinceLast: leg(),
    ...robShape(),
    weather: weatherBlock(),
    remarks: note(),
    ...flowShape(),
  }),
  // Sürüklenmede günlük: drift mesafesi kaybolmasın (ADR 0059 §3)
  drift_daily: object({ distanceSinceLast: leg(), ...dailyShape() }),
  drift_end: object({ distanceSinceLast: leg(), ...robShape(), remarks: note(), ...flowShape() }),
  strait_entry: passage(),
  strait_exit: passage(),
  canal_entry: passage(),
  canal_exit: passage(),
  cargo_start: object({ operation: option(CARGO_OPERATIONS), remarks: note() }),
  cargo_complete: object({ operation: option(CARGO_OPERATIONS), remarks: note() }),
  on_hire: object({
    charterPartyNo: z.string().trim().max(60, 'TOO_LONG').nullish(),
    remarks: note(),
  }),
  off_hire: object({
    charterPartyNo: z.string().trim().max(60, 'TOO_LONG').nullish(),
    remarks: note(),
  }),
} satisfies Record<VoyageReportKind, z.ZodType>;

export const voyageReportDataSchemas = DATA_SCHEMAS;
export type VoyageReportData<K extends VoyageReportKind = VoyageReportKind> = z.output<
  (typeof DATA_SCHEMAS)[K]
>;
export type NoonReportData = VoyageReportData<'noon'>;

const envelope = z.object({
  reportedAt: datetime(),
  /** Gemi saati farkı: dakika hassasiyeti, −12:00…+14:00 (Noon Report "Time Zone"; ADR 0065 §8). */
  tzOffsetMin: tzOffset(),
  lat: num(-90, 90).nullable(),
  lon: num(-180, 180).nullable(),
});

export interface VoyageReportInput {
  kind: VoyageReportKind;
  reportedAt: string;
  tzOffsetMin: number;
  lat: number | null;
  lon: number | null;
  data: unknown;
  /** Şema sürümü; verilmezse güncel sürüm (ADR 0058 §1). */
  schema?: number;
}

export type VoyageReport = {
  [K in VoyageReportKind]: {
    kind: K;
    reportedAt: string;
    tzOffsetMin: number;
    lat: number | null;
    lon: number | null;
    data: VoyageReportData<K>;
  };
}[VoyageReportKind];

export interface SchemaIssue {
  /** Alanın yolu, nokta ile: `data.fuel.fuels.0.consumption`. */
  path: string;
  code: string;
  /**
   * `TIME_ORDER`'da karşılaştırılan zamanın yolu: alan bundan **sonra** (`after`) ya da **önce**
   * (`before`) olmalı. Ekran hatayı "Cast Off 18.10.2026 14:20 LT'den sonra olmalı" diye yazar
   * (ortak geri bildirimi 2026-10-19).
   */
  after?: string;
  before?: string;
}

export type ParseResult<T> = { ok: true; value: T } | { ok: false; issues: SchemaIssue[] };

function toIssues(error: z.ZodError, prefix: string[] = []): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: [...prefix, ...issue.path.map(String)].join('.'),
    code: issue.message,
  }));
}

/** Aynı rapordaki zamanların sırası: her biri kendinden önce girilmiş olanlardan önce olamaz. */
function timeOrder(
  data: Record<string, unknown>,
  fields: readonly string[],
  end?: string,
): SchemaIssue[] {
  const issues: SchemaIssue[] = [];
  let latest: { field: string; value: string } | undefined;
  for (const field of fields) {
    const value = data[field];
    if (typeof value !== 'string') continue;
    if (latest && Date.parse(value) < Date.parse(latest.value)) {
      issues.push({ path: `data.${field}`, code: 'TIME_ORDER', after: `data.${latest.field}` });
      continue;
    }
    if (end && Date.parse(value) > Date.parse(end)) {
      issues.push({ path: `data.${field}`, code: 'TIME_ORDER', before: 'reportedAt' });
      continue;
    }
    latest = { field, value };
  }
  return issues;
}

/**
 * Ortak bölümlerin kuralları (ADR 0057 §3, §5): römorkörün cast off'u alongside'dan önce olamaz;
 * Next Operation türün seçeneklerinden olmalı.
 */
function sectionIssues(kind: VoyageReportKind, data: Record<string, unknown>): SchemaIssue[] {
  const issues: SchemaIssue[] = [];
  if (Array.isArray(data.tugs)) {
    (data.tugs as Record<string, unknown>[]).forEach((row, index) => {
      for (const issue of timeOrder(row, ['alongside', 'castOff'])) {
        const rowPath = (path: string) => path.replace(/^data\./, `data.tugs.${index}.`);
        issues.push({
          ...issue,
          path: rowPath(issue.path),
          ...(issue.after ? { after: rowPath(issue.after) } : {}),
        });
      }
    });
  }
  const next = data.nextOperation;
  if (
    typeof next === 'string' &&
    !(nextOperationOptions(kind) as readonly string[]).includes(next)
  ) {
    issues.push({ path: 'data.nextOperation', code: 'INVALID_OPTION' });
  }
  return issues;
}

const filled = (value: unknown) => typeof value === 'string' && value.trim() !== '';
const at = (value: unknown) => (typeof value === 'string' ? Date.parse(value) : Number.NaN);
const recordOf = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const rowsOf = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.map(recordOf) : [];
const issue = (path: string, code: string): SchemaIssue => ({ path: `data.${path}`, code });

/**
 * Yük bloğu (ADR 0058 §2): `none` satırsız, diğerleri en az bir satırlı. `strict` (Departure:
 * yapılan işlem) yüklenen ve boşaltılan miktarı ister; Berthing'de işlem planlanandır.
 */
function cargoIssues(value: unknown, strict: boolean): SchemaIssue[] {
  if (value === null || value === undefined) {
    return strict ? [issue('cargo.operation', 'REQUIRED')] : [];
  }
  const cargo = recordOf(value);
  const lines = rowsOf(cargo.lines);
  const operation = cargo.operation;
  if (!operation) return strict ? [issue('cargo.operation', 'REQUIRED')] : [];
  if (operation === 'none') return lines.length > 0 ? [issue('cargo.lines', 'NOT_ALLOWED')] : [];
  if (lines.length === 0) return [issue('cargo.lines', 'REQUIRED')];
  if (!strict) return [];
  const out: SchemaIssue[] = [];
  lines.forEach((line, index) => {
    if ((operation === 'loading' || operation === 'both') && typeof line.loadedQty !== 'number') {
      out.push(issue(`cargo.lines.${index}.loadedQty`, 'REQUIRED'));
    }
    if (
      (operation === 'discharging' || operation === 'both') &&
      typeof line.dischargedQty !== 'number'
    ) {
      out.push(issue(`cargo.lines.${index}.dischargedQty`, 'REQUIRED'));
    }
  });
  return out;
}

/**
 * Sürüm 2 Departure (ADR 0058 §1–2): olay zamanı Cast Off; pilot biniş ondan önce, pilot
 * ayrılış ondan sonra. Gönderimde zorunlular `REQUIRED` koduyla (taslakta süzülür); römorkör
 * sayısı ile satırların uyuşmazlığı yalnız gönderimde.
 */
function departureIssues(
  data: Record<string, unknown>,
  castOff: string,
  draft: boolean,
): SchemaIssue[] {
  const out: SchemaIssue[] = [];
  // Sıra hatası yalnız iki zaman da doluyken (boş alan `REQUIRED`); hangi zamana göre olduğu yazılır
  if (at(data.pilotOnBoard) > at(castOff)) {
    out.push({ ...issue('pilotOnBoard', 'TIME_ORDER'), before: 'reportedAt' });
  }
  if (at(data.pilotOff) < at(castOff)) {
    out.push({ ...issue('pilotOff', 'TIME_ORDER'), after: 'reportedAt' });
  }
  if (data.pilotNotUsed === true) {
    if (data.pilotOnBoard || data.pilotOff) out.push(issue('pilotNotUsed', 'CONFLICT'));
  } else {
    if (!data.pilotOnBoard) out.push(issue('pilotOnBoard', 'REQUIRED'));
    if (!data.pilotOff) out.push(issue('pilotOff', 'REQUIRED'));
  }
  const tugs = rowsOf(data.tugs);
  if (typeof data.tugsUsed !== 'number') out.push(issue('tugsUsed', 'REQUIRED'));
  else if (tugs.length > 0 && data.tugsUsed !== tugs.length) {
    if (!draft) out.push(issue('tugsUsed', 'MISMATCH'));
  } else if (data.tugsUsed > 0 && tugs.length === 0) out.push(issue('tugs', 'REQUIRED'));
  if (!data.draft) out.push(issue('draft.fwdM', 'REQUIRED'), issue('draft.aftM', 'REQUIRED'));
  if (!filled(data.nextPort) && !filled(data.nextPortUnknownReason)) {
    out.push(issue('nextPort', 'REQUIRED'));
  }
  out.push(...cargoIssues(data.cargo, true));
  const classification = recordOf(data.classification);
  if (!classification.employment) out.push(issue('classification.employment', 'REQUIRED'));
  if (!classification.purpose) out.push(issue('classification.purpose', 'REQUIRED'));
  if (classification.purpose === 'other' && !filled(classification.purposeOther)) {
    out.push(issue('classification.purposeOther', 'REQUIRED'));
  }
  // Karar matrisi: kiralı kalkışta kira/sefer referansı (daha sıkı okuma)
  if (classification.employment === 'chartered' && !filled(classification.charterReference)) {
    out.push(issue('classification.charterReference', 'REQUIRED'));
  }
  if (!data.voyageAction) out.push(issue('voyageAction', 'REQUIRED'));
  if (data.voyageAction === 'none' && !filled(data.noVoyageReason)) {
    out.push(issue('noVoyageReason', 'REQUIRED'));
  }
  const checklist = recordOf(data.checklist);
  if (checklist.status === 'incomplete' && !filled(checklist.remarks)) {
    out.push(issue('checklist.remarks', 'REQUIRED'));
  }
  const defects = recordOf(data.openDefects);
  if (rowsOf(defects.items).length > 0 && !filled(defects.remarks)) {
    out.push(issue('openDefects.remarks', 'REQUIRED'));
  }
  return out;
}

/** Berthing (ADR 0058 §6): pilot ayrılış binişten önce olamaz; yük planlanan işlem. */
function arrivalIssues(data: Record<string, unknown>): SchemaIssue[] {
  const out: SchemaIssue[] = [];
  if (at(data.pilotOff) < at(data.pilotOnBoard)) {
    out.push({ ...issue('pilotOff', 'TIME_ORDER'), after: 'data.pilotOnBoard' });
  }
  out.push(...cargoIssues(data.cargo, false));
  return out;
}

/**
 * Günlük raporun tutarlılığı (ADR 0059 §3): "yük yok"ta miktar girilmez; son rapordan beri,
 * toplamdan büyük olamaz (yük ve çalışma saati). Zorunluluk değil, her zaman denetlenir.
 */
function dailyIssues(data: Record<string, unknown>): SchemaIssue[] {
  const out: SchemaIssue[] = [];
  const cargo = recordOf(data.cargoProgress);
  const quantities = ['plannedQty', 'handledSinceLastQty', 'handledTotalQty'] as const;
  if (cargo.operation === 'none') {
    for (const key of quantities) {
      if (typeof cargo[key] === 'number') out.push(issue(`cargoProgress.${key}`, 'NOT_ALLOWED'));
    }
  } else if (
    typeof cargo.handledSinceLastQty === 'number' &&
    typeof cargo.handledTotalQty === 'number' &&
    cargo.handledSinceLastQty > cargo.handledTotalQty
  ) {
    out.push(issue('cargoProgress.handledSinceLastQty', 'EXCEEDS_TOTAL'));
  }
  rowsOf(data.runningHours).forEach((row, index) => {
    if (
      typeof row.sinceLastH === 'number' &&
      typeof row.totalH === 'number' &&
      row.sinceLastH > row.totalH
    ) {
      out.push(issue(`runningHours.${index}.sinceLastH`, 'EXCEEDS_TOTAL'));
    }
  });
  return out;
}

/**
 * Günlük raporun gönderim zorunluları (ADR 0059 §3): faaliyet (konum durumu); Port Daily'de liman.
 * Şemada değil, gemide **yazma anında** ve formda uygulanır (ADR 0057 §5 deseni): eski sürümlü
 * geminin Anchorage Daily paketi bunlar olmadan da kabul edilir.
 */
export function dailySubmitIssues(
  kind: VoyageReportKind,
  data: Readonly<Record<string, unknown>>,
): SchemaIssue[] {
  if (!isDailyKind(kind)) return [];
  const location = recordOf(data.location);
  const out: SchemaIssue[] = [];
  if (kind === 'port_daily' && !filled(location.port)) out.push(issue('location.port', 'REQUIRED'));
  if (!location.activity) out.push(issue('location.activity', 'REQUIRED'));
  return out;
}

/** NOR: kabul ve laytime başlangıcı verilişten önce olamaz. */
function norIssues(data: Record<string, unknown>): SchemaIssue[] {
  const nor = recordOf(data.nor);
  const out: SchemaIssue[] = [];
  for (const key of ['acceptedAt', 'laytimeCommencedAt']) {
    if (at(nor[key]) < at(nor.tenderedAt)) {
      out.push({ ...issue(`nor.${key}`, 'TIME_ORDER'), after: 'data.nor.tenderedAt' });
    }
  }
  return out;
}

/**
 * Raporu doğrular; tanımsız alanlar atılır. Konum zorunlu türlerde konumsuz rapor, aynı rapordaki
 * ters sıralı zamanlar ve listede olmayan değerler reddedilir. Raporların birbirine göre sırası
 * (COSP < Noon < EOSP) ve gelecek tarih kayıt katmanında denetlenir (gemi sunucusu saati).
 */
/** Taslakta da zorunlu olan alanlar: raporun kimliği ve zamanı. */
const ALWAYS_REQUIRED = new Set(['kind', 'reportedAt', 'tzOffsetMin']);

/**
 * @param options.draft Taslak (ADR 0022 §3): zorunlu alan eksikliği (`REQUIRED`) kabul edilir,
 *   aralık, biçim ve liste yine denetlenir; zorunluluk gönderimde denetlenir. Eksik alanı olan
 *   taslağın verisi olduğu gibi döner (şema çıktısı yok).
 */
export function parseVoyageReport(
  input: VoyageReportInput,
  options: { draft?: boolean } = {},
): ParseResult<VoyageReport> {
  if (!(VOYAGE_REPORT_KINDS as readonly string[]).includes(input.kind)) {
    return { ok: false, issues: [{ path: 'kind', code: 'INVALID' }] };
  }
  const issues: SchemaIssue[] = [];
  const version = input.schema ?? VOYAGE_REPORT_SCHEMA_VERSION;
  if (!VOYAGE_REPORT_SCHEMA_VERSIONS.includes(version)) {
    return { ok: false, issues: [{ path: 'schema', code: 'INVALID' }] };
  }
  const head = envelope.safeParse(input);
  if (!head.success) issues.push(...toIssues(head.error));
  if (POSITION_REQUIRED.has(input.kind)) {
    if (missing(input.lat)) issues.push({ path: 'lat', code: 'REQUIRED' });
    if (missing(input.lon)) issues.push({ path: 'lon', code: 'REQUIRED' });
  } else if (missing(input.lat) !== missing(input.lon)) {
    issues.push({ path: missing(input.lat) ? 'lat' : 'lon', code: 'REQUIRED' });
  }
  const schema =
    input.kind === 'departure' && version === 1 ? departureV1 : DATA_SCHEMAS[input.kind];
  const body = schema.safeParse(input.data ?? undefined);
  if (!body.success) {
    issues.push(...toIssues(body.error, ['data']));
  } else {
    const data = body.data as Record<string, unknown>;
    if (input.kind === 'departure' && version === 1) {
      issues.push(...timeOrder(data, ['pilotOnBoard'], input.reportedAt));
      issues.push(...timeOrder(data, ['letGo'], input.reportedAt));
    } else if (input.kind === 'departure') {
      issues.push(...departureIssues(data, input.reportedAt, options.draft === true));
    }
    if (input.kind === 'arrival') {
      issues.push(...timeOrder(data, ['pilotOnBoard', 'firstLine', 'allFast']));
      issues.push(...arrivalIssues(data));
    }
    if (isDailyKind(input.kind)) issues.push(...dailyIssues(data));
    issues.push(...norIssues(data));
    issues.push(...sectionIssues(input.kind, data));
  }
  const blocking = options.draft
    ? issues.filter((i) => i.code !== 'REQUIRED' || ALWAYS_REQUIRED.has(i.path))
    : issues;
  if (blocking.length > 0) return { ok: false, issues: blocking };
  if (!head.success) return { ok: false, issues };
  const data = body.success ? body.data : ((input.data ?? {}) as Record<string, unknown>);
  return { ok: true, value: { kind: input.kind, ...head.data, data } as VoyageReport };
}

/** Sefer ana kaydı (Voyage Master, "Add New Voyage"); ofis yazar (ADR 0022 §1). */
export const voyageMasterSchema = z
  .object({
    voyageNo: text(40),
    fromPort: text(),
    toPort: text(),
    /** Paketli ya da şirket listesindeki liman (ADR 0048 §3); listede yoksa yalnız metin. */
    fromPortCode: portCodeField(),
    toPortCode: portCodeField(),
    plannedDistanceNm: num(0, 30_000),
    /**
     * Planlanan kalkış ve varış (ADR 0054 §2): UTC an + limanın saat farkı. ETD ve farklar isteğe
     * bağlıdır (M14a öncesi seferler senkronla geçerli kalır); ekran ETD'yi zorunlu tutar.
     */
    plannedEtd: datetime().nullable().default(null),
    plannedEtdTzOffsetMin: tzOffset().nullable().default(null),
    plannedEta: datetime(),
    plannedEtaTzOffsetMin: tzOffset().nullable().default(null),
    charterer: z.string().trim().max(200, 'TOO_LONG').nullish(),
    charterPartyNo: z.string().trim().max(60, 'TOO_LONG').nullish(),
    cpSpeedLadenKn: speed().nullish(),
    cpSpeedBallastKn: speed().nullish(),
    cpConsumptionLadenMt: num(0, 500).nullish(),
    cpConsumptionBallastMt: num(0, 500).nullish(),
    mainEngineFuel: option(FUEL_TYPES).nullish(),
    auxEngineFuel: option(FUEL_TYPES).nullish(),
    cpMaxBeaufort: int(0, 12).nullish(),
    cpMaxWaveHeightM: num(0, 30).nullish(),
    remarks: note(),
    notes: note(),
    /**
     * Seferin "planlanan CP"si (ADR 0084 Ek 2): CP Register'daki kaydın kimliği; şartlar yalnız CP
     * kaydındadır. Eski seferde boş.
     */
    charterPartyId: z.uuid(typed('INVALID_ID')).nullish(),
    /** Charter'sız sefer için seferin Owner's reference'ı; boş alan geminin varsayılanından. */
    ownerReference: ownerReferenceSchema.nullish(),
  })
  .superRefine((value, ctx) => {
    if (value.plannedEtd !== null && Date.parse(value.plannedEta) < Date.parse(value.plannedEtd)) {
      ctx.addIssue({ code: 'custom', path: ['plannedEta'], message: 'ETA_BEFORE_ETD' });
    }
  });
export type VoyageMaster = z.output<typeof voyageMasterSchema>;

export function parseVoyageMaster(input: unknown): ParseResult<VoyageMaster> {
  const result = voyageMasterSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}
